import { useEffect, useRef } from "react";
import { useNotifications } from "../context/NotificationContext";
import { useSalon } from "../context/SalonContext";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { getClientSessionId } from "../utils/clientSession";
import { actingLocationHeaders } from "../utils/actingLocation";
import { emitMessagingRealtime } from "../utils/messagingRealtime";
import { emitPusherReconnected } from "../utils/pusherConnection";

// Events that land in the notification bell/feed. Delivered only on the
// signed-in staff member's own channel, to the people their notification
// settings say should get them. The salon channel no longer carries them.
export const FEED_NOTIFICATION_EVENTS = [
  "appointment.created",
  "appointment.updated",
  "post_appointment_form.submitted",
  "package_purchased",
  "gift_card_purchased",
  "gift_card_claimed",
  // Product release announcements (support dashboard → SQS → notifications
  // stack). Persisted like any notification; ReleaseNoteWatcher toasts it.
  "release_note.published",
];

/** Private channel carrying one staff member's notifications at one location. */
export const getStaffNotificationsChannelName = (salonId, staffUserId) =>
  `private-salon-${salonId}-staff-${staffUserId}-notifications`;

function bindFeedNotificationEvents(channel, addNotification) {
  FEED_NOTIFICATION_EVENTS.forEach((eventName) => {
    channel.bind(eventName, (data) => {
      addNotification(
        { ...data, notification_id: data?.notification_id, is_read: false },
        eventName,
      );
    });
  });
}

function unbindFeedNotificationEvents(channel) {
  FEED_NOTIFICATION_EVENTS.forEach((eventName) => channel.unbind(eventName));
}

export function usePusher() {
  const { addNotification } = useNotifications();
  const { businessInfo } = useSalon();
  const { getIdToken } = useApp();
  const { isAuthenticated, currentUser } = useAuth();
  const staffUserId = currentUser?.userId;
  const pusherRef = useRef(null);
  const channelRef = useRef(null);
  const staffChannelRef = useRef(null);
  const messagesChannelRef = useRef(null);
  const connectionStateHandlerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    // Initialize Pusher only if not already initialized
    const initializePusher = async () => {
      if (cancelled) return;
      if (
        !pusherRef.current &&
        window.Pusher &&
        businessInfo?.id &&
        isAuthenticated
      ) {
        try {
          pusherRef.current = new window.Pusher(
            `${process.env.REACT_APP_PUSHER_KEY}`,
            {
              cluster: "us2",
              authEndpoint: `${process.env.REACT_APP_NOTIFICATIONS_API_URL}/pusher/auth`,
              authorizer: (channel, options) => {
                return {
                  authorize: async (socketId, callback) => {
                    try {
                      const token = await getIdToken();
                      if (!token) {
                        throw new Error("No authentication token available");
                      }
                      const response = await fetch(
                        `${process.env.REACT_APP_NOTIFICATIONS_API_URL}/pusher/auth`,
                        {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                            ...actingLocationHeaders(),
                          },
                          body: JSON.stringify({
                            socket_id: socketId,
                            channel_name: channel.name,
                          }),
                        },
                      );
                      if (!response.ok) {
                        const errorBody = await response.text();
                        throw new Error(
                          `Pusher auth failed: ${response.status} ${errorBody}`,
                        );
                      }
                      const authData = await response.json();
                      callback(null, authData);
                    } catch (err) {
                      console.error(
                        "Pusher channel authorization failed:",
                        err,
                      );
                      callback(err, null);
                    }
                  },
                };
              },
            },
          );

          // Re-emit "socket came back" so listeners (ReleaseNoteWatcher) can
          // catch up on events published while a mobile tab was backgrounded
          // and the websocket was suspended. The first "connected" is the
          // initial connect, not a reconnect, so it is skipped.
          let hasConnected = false;
          const handleConnectionStateChange = (states) => {
            if (states?.current !== "connected") return;
            if (hasConnected) emitPusherReconnected();
            hasConnected = true;
          };
          pusherRef.current.connection?.bind?.(
            "state_change",
            handleConnectionStateChange,
          );
          connectionStateHandlerRef.current = handleConnectionStateChange;

          // Subscribe to the appointment notifications channel
          channelRef.current = pusherRef.current.subscribe(
            `private-salon-${businessInfo.id}-notifications`,
          );

          // The salon-wide channel only carries cross-client sync events now;
          // notifications arrive on the staff member's own channel below.
          // Cross-client refetch trigger for staff-driven mutations made via
          // the core/payments microservices. Does NOT surface in the
          // notifications tab — NotificationContext routes "transaction.sync"
          // to a separate `pusherSyncEvents` slice consumed by CalendarView.
          // The originating tab ignores its own echo via session id.
          channelRef.current.bind("transaction.sync", (data) => {
            if (data?.originating_session_id === getClientSessionId()) return;
            addNotification(data, "transaction.sync");
          });
          // Walk-in waitlist changed (create / edit / reorder / remove /
          // assign) on another client: NotificationContext routes it to
          // `waitlistSyncEvents` and the calendar refetches the list. Same
          // own-echo suppression by session id as transaction.sync.
          channelRef.current.bind("waitlist.sync", (data) => {
            const origin = data?.session_id ?? data?.originating_session_id;
            if (origin && origin === getClientSessionId()) return;
            addNotification(data, "waitlist.sync");
          });

          // This staff member's own notifications (Settings -> Notifications
          // decides what they receive). Pusher auth only grants your own.
          if (staffUserId) {
            staffChannelRef.current = pusherRef.current.subscribe(
              getStaffNotificationsChannelName(businessInfo.id, staffUserId),
            );
            bindFeedNotificationEvents(
              staffChannelRef.current,
              addNotification,
            );
            staffChannelRef.current.bind(
              "pusher:subscription_error",
              (error) => {
                console.warn(
                  "Could not subscribe to your notifications channel; live notifications are paused until the next reload.",
                  error,
                );
              },
            );
          }

          // Two-way messaging (Phase 6): subscribe the dedicated per-salon
          // messages channel on the SAME socket and re-emit chat events onto
          // the window for MessagingContext. Kept separate from the
          // notifications binds above so chat traffic never reaches the
          // Notification Bell. No-op for non-Twilio locations (nothing
          // publishes here for them).
          messagesChannelRef.current = pusherRef.current.subscribe(
            `private-salon-${businessInfo.id}-messages`,
          );
          messagesChannelRef.current.bind("message.received", (data) => {
            emitMessagingRealtime({
              name: "message.received",
              data: data || {},
            });
          });
          // Outbound message just went out (scheduled send firing, booking
          // confirmation, or a reply from another device/staffer). Lets an open
          // inbox/thread surface it live — there was no outbound event before.
          messagesChannelRef.current.bind("message.created", (data) => {
            emitMessagingRealtime({
              name: "message.created",
              data: data || {},
            });
          });
          messagesChannelRef.current.bind("message.status", (data) => {
            emitMessagingRealtime({ name: "message.status", data: data || {} });
          });
        } catch (error) {
          console.error("Failed to initialize Pusher:", error);
        }
      }
    };

    initializePusher();

    // Returned synchronously so React actually runs it on re-run/unmount.
    // Tears down the connection on logout, salon switch, or unmount so a
    // stale instance never stays subscribed to a previous user's channel.
    return () => {
      cancelled = true;
      if (channelRef.current) {
        channelRef.current.unbind("transaction.sync");
        channelRef.current.unbind("waitlist.sync");
        pusherRef.current?.unsubscribe(
          `private-salon-${businessInfo?.id}-notifications`,
        );
        channelRef.current = null;
      }
      if (staffChannelRef.current) {
        unbindFeedNotificationEvents(staffChannelRef.current);
        staffChannelRef.current.unbind("pusher:subscription_error");
        pusherRef.current?.unsubscribe(
          getStaffNotificationsChannelName(businessInfo?.id, staffUserId),
        );
        staffChannelRef.current = null;
      }
      if (messagesChannelRef.current) {
        messagesChannelRef.current.unbind("message.received");
        messagesChannelRef.current.unbind("message.created");
        messagesChannelRef.current.unbind("message.status");
        pusherRef.current?.unsubscribe(
          `private-salon-${businessInfo?.id}-messages`,
        );
        messagesChannelRef.current = null;
      }
      if (pusherRef.current) {
        if (connectionStateHandlerRef.current) {
          pusherRef.current.connection?.unbind?.(
            "state_change",
            connectionStateHandlerRef.current,
          );
          connectionStateHandlerRef.current = null;
        }
        pusherRef.current.disconnect();
        pusherRef.current = null;
      }
    };
  }, [
    addNotification,
    businessInfo?.timezone,
    businessInfo?.id,
    isAuthenticated,
    staffUserId,
  ]);

  return {
    isConnected: !!pusherRef.current,
  };
}
